import Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { SunIcon, MoonIcon, ComputerDesktopIcon } from "@heroicons/react/24/outline";

export interface SwitchDarkModeProps {
  className?: string;
}

type ThemeMode = "light" | "dark" | "system";

const SwitchDarkMode: React.FC<SwitchDarkModeProps> = ({ className = "" }) => {
  const [mode, setMode] = useState<ThemeMode>(
    (Cookies.get("theme") as ThemeMode) || "system"
  );

  const applyTheme = (themeMode: ThemeMode) => {
    const root = document.documentElement;
    const isDark =
      themeMode === "dark" ||
      (themeMode === "system" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    if (isDark) {
      root.classList.add("dark");
      root.classList.remove("light");
      document.body.classList.add("dark");
      document.body.classList.remove("light");
    } else {
      root.classList.remove("dark");
      root.classList.add("light");
      document.body.classList.remove("dark");
      document.body.classList.add("light");
    }

    // Update theme-color meta tag
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", isDark ? "#0F172A" : "#FFFFFF");
    }
  };

  useEffect(() => {
    applyTheme(mode);

    if (mode === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme("system");
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
  }, [mode]);

  const cycleTheme = () => {
    const next: ThemeMode =
      mode === "light" ? "dark" : mode === "dark" ? "system" : "light";
    setMode(next);
    Cookies.set("theme", next);
  };

  const icon =
    mode === "light" ? (
      <SunIcon className="w-5 h-5" />
    ) : mode === "dark" ? (
      <MoonIcon className="w-5 h-5" />
    ) : (
      <ComputerDesktopIcon className="w-5 h-5" />
    );

  const label =
    mode === "light" ? "Light" : mode === "dark" ? "Dark" : "System";

  return (
    <button
      onClick={cycleTheme}
      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none flex items-center justify-center transition-colors ${className}`}
      title={`Theme: ${label}`}
      aria-label={`Switch theme, currently ${label}`}
    >
      {icon}
    </button>
  );
};

export default SwitchDarkMode;
